// src/pages/CourseAnalyticsPage.jsx
// 🎓 TICKET GHTVN9 - Panel de analítica por curso (MVP)
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, TrendingUp, Clock, CheckCircle, Bot, FlaskConical, 
  BookOpen, Award, BarChart3, Activity 
} from 'lucide-react';
import { getCourseById } from '../courses/CourseCatalog';
import { fetchCourseAnalytics } from '../courses/courseAnalyticsApi';

const CourseAnalyticsPage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const course = getCourseById(courseId);
  
  const [analytics, setAnalytics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadAnalytics = async () => {
      setIsLoading(true);
      setError(null);
      
      const result = await fetchCourseAnalytics(courseId);
      
      if (result.success) {
        setAnalytics(result.data);
      } else {
        setError(result.error);
      }
      
      setIsLoading(false);
    };
    
    if (courseId) {
      loadAnalytics();
    }
  }, [courseId]);

  // Helper: Formatear segundos a formato legible
  const formatDuration = (seconds) => {
    if (seconds === 0) return '—';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  // Helper: Formatear timestamp
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Justo ahora';
    if (diffMins < 60) return `Hace ${diffMins}m`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `Hace ${diffHours}h`;
    
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `Hace ${diffDays}d`;
    
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  };

  // Helper: Icono por tipo de actividad
  const getActivityIcon = (type) => {
    switch (type) {
      case 'vrt_simulation':
        return <Bot className="w-4 h-4" />;
      case 'epl_lab':
        return <FlaskConical className="w-4 h-4" />;
      case 'resource':
        return <BookOpen className="w-4 h-4" />;
      default:
        return <Activity className="w-4 h-4" />;
    }
  };

  // Helper: Label por tipo de actividad
  const getActivityTypeLabel = (type) => {
    switch (type) {
      case 'vrt_simulation':
        return 'Simulación VRT';
      case 'epl_lab':
        return 'Lab EPL';
      case 'resource':
        return 'Recurso';
      default:
        return type;
    }
  };

  // Helper: Badge color por tipo de evento
  const getEventBadge = (eventType) => {
    if (eventType === 'complete') {
      return <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">Completado</span>;
    }
    return <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">Iniciado</span>;
  };

  if (!course) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl p-8 text-center">
            <p className="text-gray-600">Curso no encontrado</p>
            <button
              onClick={() => navigate('/courses')}
              className="mt-4 text-blue-600 hover:underline"
            >
              Volver al dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-6xl mx-auto">
          {/* Header skeleton */}
          <div className="mb-6 animate-pulse">
            <div className="h-8 w-48 bg-gray-300 rounded mb-2"></div>
            <div className="h-4 w-64 bg-gray-200 rounded"></div>
          </div>
          
          {/* Cards skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-2xl p-6 animate-pulse">
                <div className="h-4 w-24 bg-gray-200 rounded mb-4"></div>
                <div className="h-8 w-16 bg-gray-300 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 rounded-2xl p-8 text-center">
            <p className="text-red-600 mb-4">Error al cargar analítica: {error}</p>
            <button
              onClick={() => navigate(`/courses/${courseId}`)}
              className="text-red-600 hover:underline"
            >
              Volver al curso
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { summary, modules, recentActivity } = analytics;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(`/courses/${courseId}`)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver al curso
          </button>
          
          <div className="flex items-start gap-3 mb-2">
            <div className="p-3 bg-blue-100 rounded-xl">
              <BarChart3 className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Analítica del curso
              </h1>
              <p className="text-gray-600 mt-1">{course.title}</p>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Card: Progreso */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <span className="text-sm font-medium text-gray-600">Progreso</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-bold text-gray-900">
                {summary.completionPercent}%
              </span>
              <span className="text-sm text-gray-500">
                ({summary.completedActivities}/{summary.totalActivities})
              </span>
            </div>
          </div>

          {/* Card: Tiempo total */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-sm font-medium text-gray-600">Tiempo total</span>
            </div>
            <div className="text-4xl font-bold text-gray-900">
              {formatDuration(summary.totalTimeSeconds)}
            </div>
          </div>

          {/* Card: Práctica */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Award className="w-5 h-5 text-purple-600" />
              </div>
              <span className="text-sm font-medium text-gray-600">Práctica</span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">VRT:</span>
                <span className="font-medium">{summary.vrtSessionsCompleted}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">EPL:</span>
                <span className="font-medium">{summary.eplLabsCompleted}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Recursos:</span>
                <span className="font-medium">{summary.resourcesCompleted}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Module Progress */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            Progreso por módulo
          </h2>
          
          <div className="space-y-4">
            {modules.map((module, idx) => (
              <div key={module.moduleId} className="border-b border-gray-100 pb-4 last:border-0 last:pb-0">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{module.moduleTitle}</h3>
                    <p className="text-sm text-gray-500">
                      {module.completedActivities}/{module.totalActivities} actividades • {formatDuration(module.timeSeconds)}
                    </p>
                  </div>
                  <span className="text-lg font-bold text-blue-600">
                    {module.completionPercent}%
                  </span>
                </div>
                
                {/* Progress bar */}
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${module.completionPercent}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-purple-600" />
            Actividad reciente
          </h2>
          
          {recentActivity.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              No hay actividad registrada aún
            </p>
          ) : (
            <div className="space-y-3">
              {recentActivity.map((event, idx) => (
                <div 
                  key={`${event.activityId}-${event.timestamp}-${idx}`}
                  className="flex items-center gap-4 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="p-2 bg-gray-100 rounded-lg">
                    {getActivityIcon(event.activityType)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">
                      {event.activityId}
                    </p>
                    <p className="text-sm text-gray-500">
                      {getActivityTypeLabel(event.activityType)} • {formatDuration(event.durationSeconds)}
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    {getEventBadge(event.eventType)}
                    <span className="text-xs text-gray-400 whitespace-nowrap">
                      {formatTimestamp(event.timestamp)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseAnalyticsPage;
